// src/app/new-customer.jsx
import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
} from 'react-native';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdRemove } from 'react-icons/md';
import API_BASE_URL from '../apiConfig.js';
import { useEffect } from 'react';

// Helper to fetch a token from localStorage
const getToken = () => {
  try {
    return localStorage.getItem('userToken');
  } catch {
    return null;
  }
};

export default function NewCustomer() {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [stateVal, setStateVal] = useState('');
  const [zip, setZip] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [userPermissions, setUserPermissions] = useState(null);

  const [isNotesVisible, setIsNotesVisible] = useState(false);
  const [notes, setNotes] = useState('');
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);
          
          const user_type = parsedPermissions?.user_type;
          const customerPerm = parsedPermissions?.customer_perm;
          
          if (user_type !== 'Owner' && user_type !== 'Admin' && customerPerm !== 'write') {
            alert("You don't have permission to add customers. Please contact your account administrator.");
            navigate('/'); 
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);

  const saveCustomer = async () => {
    if (!name?.trim()) {
      window.alert('Customer name is required. Please enter a name.');
      return;
    }

    try {
      const token = getToken();
      const customerPayload = {
        name: name.trim(),
        address: address?.trim() || '',
        city: city?.trim() || '',
        state: stateVal?.trim() || '',
        zip: zip?.trim() || '',
        phone: phone?.trim() || '',
        email: email?.trim() || null,
        notes: notes?.trim() || '',
      };

      await axios.post(`${API_BASE_URL}/api/customers`, customerPayload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      navigate('/view-customers', { replace: true });
    } catch (error) {
      console.error('Error adding customer:', error);
      const detail = error.response?.data?.detail;
      const message = error.response?.data?.error ||
        (Array.isArray(detail)
          ? detail.map((issue) => issue.msg).join('\n')
          : detail) ||
        'An error occurred while adding the customer.';
      window.alert(message);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      style={{ flex: 1 }}
    >
      <ScrollView
        contentContainerStyle={styles.container}
        keyboardShouldPersistTaps="handled"
      >
          <Text style={styles.header}>Add New Customer</Text>

          <Text style={styles.label}>Name</Text>
          <TextInput style={styles.input} value={name} onChangeText={setName} />

          <Text style={styles.label}>Address</Text>
          <TextInput
            style={styles.input}
            value={address}
            onChangeText={setAddress}
          />

          <Text style={styles.label}>City</Text>
          <TextInput style={styles.input} value={city} onChangeText={setCity} />

          <Text style={styles.label}>State</Text>
          <TextInput
            style={styles.input}
            value={stateVal}
            onChangeText={setStateVal}
          />

          <Text style={styles.label}>Zip</Text>
          <TextInput
            style={styles.input}
            value={zip}
            onChangeText={setZip}
            keyboardType="numeric"
          />

          <Text style={styles.label}>Phone</Text>
          <TextInput
            style={styles.input}
            value={phone}
            onChangeText={setPhone}
            keyboardType="phone-pad"
          />

          <Text style={styles.label}>Email</Text>
          <TextInput
            style={styles.input}
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
          />

          {!isNotesVisible ? (
            <TouchableOpacity
              style={styles.addNotesButton}
              onPress={() => setIsNotesVisible(true)}
            >
              <MdAdd size={20} color="green" />
              <Text style={styles.addNotesText}>Add Notes</Text>
            </TouchableOpacity>
          ) : (
            <>
              <TouchableOpacity
                style={styles.hideNotesButton}
                onPress={() => setIsNotesVisible(false)}
              >
                <MdRemove size={20} color="red" />
                <Text style={styles.hideNotesText}>Hide Notes</Text>
              </TouchableOpacity>

              <View style={styles.fieldContainer}>
                <Text style={styles.label}>Notes:</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  placeholder="Enter notes here..."
                  multiline
                  numberOfLines={4}
                  onChangeText={setNotes}
                  value={notes}
                />
              </View>
            </>
          )}

          <TouchableOpacity style={styles.saveButton} onPress={saveCustomer}>
            <Text style={styles.saveButtonText}>Add Customer</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.cancelButton}
            onPress={() => navigate(-1)}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: { padding: 20, paddingBottom: 50 },
  header: { fontSize: 20, fontWeight: 'bold', marginBottom: 15 },
  label: { marginTop: 15, fontWeight: 'bold' },
  input: {
    borderWidth: 1,
    borderColor: '#ccc',
    padding: 8,
    borderRadius: 4,
    marginTop: 5,
    backgroundColor: '#fff',
  },
  saveButton: {
    backgroundColor: '#007AFF',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    marginTop: 20,
  },
  saveButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
  addNotesButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 20,
  },
  addNotesText: { color: 'green', marginLeft: 5, fontSize: 16 },
  hideNotesButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 20,
  },
  hideNotesText: { color: 'red', marginLeft: 5, fontSize: 16 },
  fieldContainer: { marginTop: 0 },
  textArea: { height: 100, textAlignVertical: 'top' },
  cancelButton: {
    backgroundColor: '#FF3B30',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    marginTop: 10,
  },
  cancelButtonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
});
